import { memo, useCallback, useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { HexColorPicker } from "react-colorful";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPalette,
  faEyeDropper,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { TooltipProvider } from "@/components/ui/tooltip";

import useHistoryStore from "@/store/useHistoryStore";

const remToPx = (value) => {
  if (typeof value !== "string") return null;
  if (value.endsWith("rem")) {
    return parseFloat(value) * 16;
  }
  if (value.endsWith("px")) {
    return parseFloat(value);
  }
  return null;
};

const getValueInPx = (value) => {
  if (!value) return 24; // Default to 24px if no value
  if (typeof value === "number") return value;
  const pxValue = remToPx(value) || parseInt(value) || 24; // Default to 24px if parsing fails
  return isNaN(pxValue) ? 24 : pxValue; // Ensure we never return NaN
};

// Helper function to get display name for widget types
const getWidgetTypeName = (widgetType) => {
  const typeMap = {
    bio: "Bio",
    rich_text: "Rich Text",
    single_media: "Single Media",
    image_gallery: "Image Gallery",
    links: "Links",
    mini_link: "Mini Link",
    business_hours: "Business Hours",
    file: "File",
    video_player: "Video Player",
    spotify: "Spotify",
    apple_music: "Apple Music",
    calendly: "Calendly",
    opentable: "OpenTable",
    map: "Map",
    divider: "Divider",
    location: "Location",
    multi_link: "Multi Link",
    plain_text: "Plain Text",
    markdown: "Markdown",
    photo_album: "Photo Album",
  };

  return (
    typeMap[widgetType] ||
    widgetType.charAt(0).toUpperCase() + widgetType.slice(1).replace(/_/g, " ")
  );
};

const LayoutSettings = function LayoutSettings({
  widget,
  handleWidgetUpdate,
  allWidgets,
}) {
  const [currentStep, setCurrentStep] = useState("main");
  const [activeColorSetting, setActiveColorSetting] = useState(null);
  const [isPickingColor, setIsPickingColor] = useState(false);
  const { settings = {} } = widget.layout;
  const pushAction = useHistoryStore((state) => state.pushAction);

  // Local state for real-time visual feedback
  const [localTransparency, setLocalTransparency] = useState(() =>
    Math.max(0, Math.min(100, parseInt(settings.transparency || "100")))
  );

  // Local state for border radius
  const [localBorderRadius, setLocalBorderRadius] = useState(() =>
    getValueInPx(settings.borderRadius)
  );

  // Local state for border width (defaults to 0, not 24 like border radius)
  const [localBorderWidth, setLocalBorderWidth] = useState(() => {
    if (!settings.borderWidth) return 0;
    if (typeof settings.borderWidth === "number") return settings.borderWidth;
    const pxValue =
      remToPx(settings.borderWidth) || parseInt(settings.borderWidth);
    return isNaN(pxValue) ? 0 : pxValue;
  });

  // Local state for colors to enable real-time visual feedback
  const [localColors, setLocalColors] = useState({
    backgroundColor: settings.backgroundColor || "#ffffff",
    borderColor: settings.borderColor || "#000000",
    headerColor: settings.headerColor || "#000000",
    paragraphColor: settings.paragraphColor || "#000000",
  });

  // Track user interaction states
  const [isDraggingTransparency, setIsDraggingTransparency] = useState(false);
  const [isDraggingColorPicker, setIsDraggingColorPicker] = useState(false);

  // Sync local state when widget settings change (from external updates)
  useEffect(() => {
    const newTransparency = Math.max(
      0,
      Math.min(100, parseInt(settings.transparency || "100"))
    );
    setLocalTransparency(newTransparency);

    // Sync border radius
    setLocalBorderRadius(getValueInPx(settings.borderRadius));

    // Sync border width (defaults to 0, not 24 like border radius)
    if (!settings.borderWidth) {
      setLocalBorderWidth(0);
    } else if (typeof settings.borderWidth === "number") {
      setLocalBorderWidth(settings.borderWidth);
    } else {
      const pxValue =
        remToPx(settings.borderWidth) || parseInt(settings.borderWidth);
      setLocalBorderWidth(isNaN(pxValue) ? 0 : pxValue);
    }

    // Update local colors when settings change
    setLocalColors({
      backgroundColor: settings.backgroundColor || "#ffffff",
      borderColor: settings.borderColor || "#000000",
      headerColor: settings.headerColor || "#000000",
      paragraphColor: settings.paragraphColor || "#000000",
    });
  }, [
    settings.transparency,
    settings.backgroundColor,
    settings.borderColor,
    settings.headerColor,
    settings.paragraphColor,
    settings.borderRadius,
  ]);

  // Debounced update references
  const debouncedUpdateRef = useRef(null);
  const colorPickerDebounceRef = useRef(null);
  const transparencyDebounceRef = useRef(null);

  // Real-time update function for visual feedback during dragging (NO database operations)
  const updateWidgetSettingsRealTime = useCallback(
    (key, value) => {
      // Don't apply border settings to single_media widgets
      if (
        widget.widget_type.type === "single_media" &&
        (key === "borderColor" || key === "borderWidth")
      ) {
        return;
      }

      // Get current widget and settings
      const currentWidget = widget;
      const currentSettings = currentWidget.layout.settings || {};

      // Handle transparency specifically
      let updatedSettings = { ...currentSettings };

      if (key === "transparency") {
        // Always update transparency value - store as string for consistency
        const transparencyString = String(value);
        updatedSettings = {
          ...updatedSettings,
          [key]: transparencyString,
        };
      } else {
        updatedSettings = {
          ...updatedSettings,
          [key]: value,
        };
      }

      // Update the widget state immediately for real-time visual feedback
      // Create a merged settings object that includes all current local state
      const previewSettings = {
        ...currentSettings,
        ...localColors, // Include all current colors
        ...(localBorderRadius !== undefined && {
          borderRadius: `${localBorderRadius}px`,
        }),
        ...(localBorderWidth !== undefined && {
          borderWidth: `${localBorderWidth}px`,
        }),
        ...(localTransparency !== undefined && {
          transparency: localTransparency.toString(),
        }),
        [key]: value, // Add the new value being changed
      };

      if (typeof handleWidgetUpdate === "function") {
        console.log(
          `🎨 [RealTime] Calling handleWidgetUpdate with isRealTimeUpdate=true for ${key}:`,
          value
        );
        handleWidgetUpdate(currentWidget.id, {
          layout: {
            ...currentWidget.layout,
            settings: previewSettings,
          },
          isPreview: true, // Use isPreview flag instead of isRealTimeUpdate
          skipHistory: true,
          triggerAutosave: false,
        });
      }

      console.log(
        `🎨 [Real-time] ${key} changed to ${value} (widget updated, no DB operations)`
      );
      console.log(
        `🎨 [Real-time] isDraggingColorPicker: ${isDraggingColorPicker}, isDraggingTransparency: ${isDraggingTransparency}`
      );
    },
    [widget, handleWidgetUpdate, isDraggingColorPicker, isDraggingTransparency]
  );

  // Core update function that handles all setting changes
  const updateWidgetSettings = useCallback(
    (key, value) => {
      // Don't apply border settings to single_media widgets
      if (
        widget.widget_type.type === "single_media" &&
        (key === "borderColor" || key === "borderWidth")
      ) {
        return;
      }

      // Get current widget and settings
      const currentWidget = widget;
      const currentSettings = currentWidget.layout.settings || {};

      // Create base settings with all current values
      // Always include current local state for colors, border radius, and border width
      // to prevent reverting when saving other settings
      let updatedSettings = {
        ...currentSettings,
        ...localColors, // Always include all current colors
        // Always include current border values to prevent reverting
        ...(localBorderRadius !== undefined && {
          borderRadius: `${localBorderRadius}px`,
        }),
        ...(localBorderWidth !== undefined && {
          borderWidth: `${localBorderWidth}px`,
        }),
        ...(localTransparency !== undefined && {
          transparency: localTransparency.toString(),
        }),
      };

      // Handle specific value updates (override with the new value)
      if (key === "transparency") {
        updatedSettings.transparency = String(value);
      } else if (key === "borderRadius") {
        updatedSettings.borderRadius = value;
      } else if (key === "borderWidth") {
        updatedSettings.borderWidth = value;
      }

      // Update current widget with all settings
      handleWidgetUpdate(currentWidget.id, {
        layout: {
          ...currentWidget.layout,
          settings: updatedSettings,
        },
      });
    },
    [
      widget,
      handleWidgetUpdate,
      pushAction,
      localColors,
      localBorderRadius,
      localBorderWidth,
      localTransparency,
    ]
  );

  // Pure visual feedback function - uses global window object for immediate preview
  const updateVisualFeedback = useCallback(
    (key, value) => {
      // Update local color state for the settings UI
      setLocalColors((prev) => ({
        ...prev,
        [key]: value,
      }));

      // Store preview settings globally so widgets can access them
      if (typeof window !== "undefined") {
        if (!window.widgetPreviewSettings) {
          window.widgetPreviewSettings = {};
        }
        if (!window.widgetPreviewSettings[widget.id]) {
          window.widgetPreviewSettings[widget.id] = {};
        }

        window.widgetPreviewSettings[widget.id][key] =
          key === "transparency" ? value.toString() : value;

        // Trigger a custom event to notify widgets of preview changes
        window.dispatchEvent(
          new CustomEvent("widgetPreviewUpdate", {
            detail: { widgetId: widget.id, key, value },
          })
        );
      }

      console.log(`🎨 [Preview] ${key} set to ${value} - NO SAVES`);
    },
    [widget.id]
  );

  // Clear preview when component unmounts or widget changes
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.widgetPreviewSettings) {
        delete window.widgetPreviewSettings[widget.id];
      }
    };
  }, [widget.id]);

  // Handle color picker interactions with real-time feedback and release-based autosave
  const handleColorPickerChange = useCallback(
    (key, value) => {
      console.log("🎨 [Color Change]", {
        key,
        value,
        currentColors: localColors,
        currentSettings: widget.layout.settings,
      });
      // Always update local state for immediate visual feedback
      updateVisualFeedback(key, value);

      // Also call the real-time widget update for immediate visual feedback
      updateWidgetSettingsRealTime(key, value);

      // NEVER save during color picker changes - only on release
      console.log(`🎨 [ColorPicker] Visual feedback only for ${key}:`, value);
    },
    [updateVisualFeedback, updateWidgetSettingsRealTime]
  );

  // Handle color picker start (when user starts dragging)
  const handleColorPickerStart = useCallback(() => {
    setIsDraggingColorPicker(true);
    // Clear any pending debounced update
    if (colorPickerDebounceRef.current) {
      clearTimeout(colorPickerDebounceRef.current);
    }
  }, []);

  // Handle color picker end (when user releases) - immediate save
  const handleColorPickerEnd = useCallback(
    (key) => {
      setIsDraggingColorPicker(false);

      // Clear any pending debounced update
      if (colorPickerDebounceRef.current) {
        clearTimeout(colorPickerDebounceRef.current);
        colorPickerDebounceRef.current = null;
      }

      // Create a merged settings object with all current colors AND border values
      // This prevents border settings from reverting when colors are saved
      const mergedSettings = {
        ...widget.layout.settings,
        ...localColors,
        // Always include current border values to prevent reverting
        ...(localBorderRadius !== undefined && {
          borderRadius: `${localBorderRadius}px`,
        }),
        ...(localBorderWidth !== undefined && {
          borderWidth: `${localBorderWidth}px`,
        }),
        ...(localTransparency !== undefined && {
          transparency: localTransparency.toString(),
        }),
      };

      // Update all settings at once
      handleWidgetUpdate(widget.id, {
        layout: {
          ...widget.layout,
          settings: mergedSettings,
        },
      });

      // Clear preview state after save
      if (
        typeof window !== "undefined" &&
        window.widgetPreviewSettings?.[widget.id]
      ) {
        delete window.widgetPreviewSettings[widget.id][key];
        if (Object.keys(window.widgetPreviewSettings[widget.id]).length === 0) {
          delete window.widgetPreviewSettings[widget.id];
        }
      }
    },
    [
      localColors,
      localBorderRadius,
      localBorderWidth,
      localTransparency,
      widget.id,
      widget.layout,
      handleWidgetUpdate,
    ]
  );

  // Handle text input color changes with real-time feedback
  const handleColorInputChange = useCallback(
    (key, value) => {
      // Use the visual feedback function for immediate updates - NO SAVES
      updateVisualFeedback(key, value);

      // Also call the real-time widget update for immediate visual feedback
      updateWidgetSettingsRealTime(key, value);

      console.log(`🎨 [TextInput] Visual feedback only for ${key}:`, value);
    },
    [updateVisualFeedback, updateWidgetSettingsRealTime]
  );

  // Handle transparency slider changes with real-time feedback
  const handleTransparencyChange = useCallback(
    (value) => {
      const transparencyValue = value[0];
      setLocalTransparency(transparencyValue);

      // ALWAYS use visual feedback only - never save during slider changes
      console.log(
        `🎨 [Transparency] Visual feedback only for transparency:`,
        transparencyValue
      );
      updateVisualFeedback("transparency", transparencyValue);
    },
    [updateVisualFeedback]
  );

  // Handle transparency slider start
  const handleTransparencyStart = useCallback(() => {
    setIsDraggingTransparency(true);
    // Clear any pending debounced update
    if (transparencyDebounceRef.current) {
      clearTimeout(transparencyDebounceRef.current);
    }
  }, []);

  // Handle transparency slider end - immediate save
  const handleTransparencyEnd = useCallback(() => {
    setIsDraggingTransparency(false);

    // Clear any pending debounced update
    if (transparencyDebounceRef.current) {
      clearTimeout(transparencyDebounceRef.current);
      transparencyDebounceRef.current = null;
    }

    // Save immediately when user releases slider
    console.log(
      `💾 [Save] Transparency slider released - saving:`,
      localTransparency
    );
    updateWidgetSettings("transparency", localTransparency.toString());

    // Clear preview state after save
    if (
      typeof window !== "undefined" &&
      window.widgetPreviewSettings?.[widget.id]
    ) {
      delete window.widgetPreviewSettings[widget.id].transparency;
      if (Object.keys(window.widgetPreviewSettings[widget.id]).length === 0) {
        delete window.widgetPreviewSettings[widget.id];
      }
    }
  }, [localTransparency, updateWidgetSettings]);

  // Handle other setting changes with debouncing
  const handleSettingChange = useCallback(
    (key, value) => {
      // Clear existing timeout
      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }

      // Set new timeout for debounced update
      debouncedUpdateRef.current = setTimeout(() => {
        updateWidgetSettings(key, value);
      }, 300);
    },
    [updateWidgetSettings]
  );

  // Get current color value (prioritize local state for real-time feedback)
  const getCurrentColor = useCallback(
    (key) => {
      // Get the appropriate default value based on the color type
      let defaultValue = "#ffffff";
      if (key === "headerColor" || key === "paragraphColor") {
        defaultValue = "#000000"; // Default text colors should be black
      } else if (key === "borderColor") {
        defaultValue = "#000000"; // Default border color should be black
      } else if (key === "backgroundColor") {
        defaultValue = "#ffffff"; // Default background color should be white
      }

      // Always prioritize the actual settings value over defaults to prevent reverting
      return localColors[key] || settings[key] || defaultValue;
    },
    [localColors, settings]
  );

  // Get current transparency value (prioritize local state for real-time feedback)
  const getCurrentTransparency = useCallback(() => {
    return localTransparency;
  }, [localTransparency]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }
      if (colorPickerDebounceRef.current) {
        clearTimeout(colorPickerDebounceRef.current);
      }
      if (transparencyDebounceRef.current) {
        clearTimeout(transparencyDebounceRef.current);
      }
    };
  }, []);

  // Add a handler to stop propagation on all interactions
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  const startColorPicking = useCallback(async () => {
    try {
      if (!window.EyeDropper) {
        console.warn("EyeDropper API not supported");
        return;
      }

      const eyeDropper = new window.EyeDropper();
      setIsPickingColor(true);

      const result = await eyeDropper.open();
      if (result.sRGBHex && activeColorSetting) {
        // Update local color immediately and save
        setLocalColors((prev) => ({
          ...prev,
          [activeColorSetting.key]: result.sRGBHex,
        }));

        // Save immediately for eye dropper
        updateWidgetSettings(activeColorSetting.key, result.sRGBHex);
      }
    } catch (error) {
      console.error("Error picking color:", error);
    } finally {
      setIsPickingColor(false);
    }
  }, [activeColorSetting, updateWidgetSettings]);

  const renderMainStep = () => (
    <div className="w-full space-y-4">
      <div
        role="button"
        className="w-full py-3 px-4 text-white bg-white/5 border border-white/10 rounded-md hover:bg-white/10 transition-colors flex justify-between items-center cursor-pointer"
        onClick={() => setCurrentStep("widget-colors")}
      >
        <span className="flex-1 text-left">Widget Colors</span>
        <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 opacity-50" />
      </div>

      <div
        role="button"
        className="w-full py-3 px-4 text-white bg-white/5 border border-white/10 rounded-md hover:bg-white/10 transition-colors flex justify-between items-center cursor-pointer"
        onClick={() => setCurrentStep("text-colors")}
      >
        <span className="flex-1 text-left">Text Colors</span>
        <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 opacity-50" />
      </div>

      <div
        role="button"
        className="w-full py-3 px-4 text-white bg-white/5 border border-white/10 rounded-md hover:bg-white/10 transition-colors flex justify-center items-center cursor-pointer"
        onClick={() => setCurrentStep("advanced")}
      >
        Advanced Settings
      </div>
    </div>
  );

  const renderColorPickerStep = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setCurrentStep(activeColorSetting.backStep);
          }}
          className="flex items-center text-white/50 hover:text-white cursor-pointer text-sm"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3 mr-1" />
          <span>Back</span>
        </div>
        <span className="text-sm font-medium text-white/70 ml-2">
          Edit {activeColorSetting.label}
        </span>
      </div>

      <div className="space-y-4">
        {getCurrentTransparency() === 0 &&
        activeColorSetting?.key === "backgroundColor" ? (
          <div className="bg-white/5 rounded-xl p-4">
            {/* Color picker for 0% transparency */}
            <div className="space-y-4">
              <div className="relative">
                <div
                  onPointerDown={handleColorPickerStart}
                  onPointerUp={() =>
                    handleColorPickerEnd(activeColorSetting.key)
                  }
                  onPointerLeave={() =>
                    handleColorPickerEnd(activeColorSetting.key)
                  }
                >
                  <HexColorPicker
                    color={getCurrentColor(activeColorSetting.key)}
                    onChange={(color) =>
                      handleColorPickerChange(activeColorSetting.key, color)
                    }
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border border-white/10 flex-shrink-0"
                  style={{
                    backgroundColor: getCurrentColor(activeColorSetting.key),
                  }}
                />
                <Input
                  type="text"
                  value={getCurrentColor(activeColorSetting.key)}
                  onChange={(e) =>
                    handleColorInputChange(
                      activeColorSetting.key,
                      e.target.value
                    )
                  }
                  className="flex-1 bg-white/5 border-white/10 text-white font-mono text-sm"
                  placeholder="#ffffff"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    startColorPicking();
                  }}
                  disabled={isPickingColor}
                  className={`p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors flex-shrink-0 ${
                    isPickingColor ? "bg-white/20" : "bg-white/5"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faEyeDropper}
                    className={`h-4 w-4 ${
                      isPickingColor ? "text-white" : "text-white/50"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/5 rounded-xl p-4 space-y-4">
            <div className="relative">
              <div
                onPointerDown={handleColorPickerStart}
                onPointerUp={() => handleColorPickerEnd(activeColorSetting.key)}
                onPointerLeave={() =>
                  handleColorPickerEnd(activeColorSetting.key)
                }
              >
                <HexColorPicker
                  color={getCurrentColor(activeColorSetting.key)}
                  onChange={(color) =>
                    handleColorPickerChange(activeColorSetting.key, color)
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg border border-white/10 flex-shrink-0"
                style={{
                  backgroundColor: getCurrentColor(activeColorSetting.key),
                }}
              />
              <Input
                type="text"
                value={getCurrentColor(activeColorSetting.key)}
                onChange={(e) =>
                  handleColorInputChange(activeColorSetting.key, e.target.value)
                }
                className="flex-1 bg-white/5 border-white/10 text-white font-mono text-sm"
                placeholder="#ffffff"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  startColorPicking();
                }}
                disabled={isPickingColor}
                className={`p-3 rounded-lg border border-white/10 hover:bg-white/10 transition-colors flex-shrink-0 ${
                  isPickingColor ? "bg-white/20" : "bg-white/5"
                }`}
              >
                <FontAwesomeIcon
                  icon={faEyeDropper}
                  className={`h-4 w-4 ${
                    isPickingColor ? "text-white" : "text-white/50"
                  }`}
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderWidgetColorStep = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setCurrentStep("main");
          }}
          className="flex items-center text-white/50 hover:text-white cursor-pointer text-sm"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3 mr-1" />
          <span>Back</span>
        </div>
        <span className="text-sm font-medium text-white/70 ml-2">
          Choose widget colors
        </span>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex justify-between items-center mb-3">
            <Label className="text-white text-sm">Background color</Label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                value={getCurrentColor("backgroundColor")}
                onChange={(e) => {
                  handleColorInputChange("backgroundColor", e.target.value);
                }}
                className="bg-white/10 border-white/20 text-white font-mono text-sm w-24 text-center"
                placeholder="#ffffff"
              />
              <div
                className="w-6 h-6 rounded-full border-2 border-white/20 cursor-pointer hover:scale-110 transition-transform"
                style={{
                  backgroundColor: getCurrentColor("backgroundColor"),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setActiveColorSetting({
                    key: "backgroundColor",
                    label: "Background Color",
                    backStep: "widget-colors",
                  });
                  setCurrentStep("color-picker");
                }}
              />
            </div>
          </div>
        </div>

        {widget.widget_type.type !== "single_media" && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-white text-sm">Border color</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="text"
                  value={getCurrentColor("borderColor")}
                  onChange={(e) => {
                    handleColorInputChange("borderColor", e.target.value);
                  }}
                  className="bg-white/10 border-white/20 text-white font-mono text-sm w-24 text-center"
                  placeholder="#000000"
                />
                <div
                  className="w-6 h-6 rounded-full border-2 border-white/20 cursor-pointer hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: getCurrentColor("borderColor"),
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    setActiveColorSetting({
                      key: "borderColor",
                      label: "Border Color",
                      backStep: "widget-colors",
                    });
                    setCurrentStep("color-picker");
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTextColorStep = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setCurrentStep("main");
          }}
          className="flex items-center text-white/50 hover:text-white cursor-pointer text-sm"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3 mr-1" />
          <span>Back</span>
        </div>
        <span className="text-sm font-medium text-white/70 ml-2">
          Text Colors
        </span>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex justify-between items-center mb-3">
            <Label className="text-white text-sm">Header color</Label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                value={getCurrentColor("headerColor")}
                onChange={(e) => {
                  handleColorInputChange("headerColor", e.target.value);
                }}
                onBlur={(e) => {
                  // Save immediately when user finishes typing
                  console.log(
                    `💾 [Save] Header color input blur - saving:`,
                    e.target.value
                  );
                  updateWidgetSettings("headerColor", e.target.value);

                  // Clear preview state after save
                  if (
                    typeof window !== "undefined" &&
                    window.widgetPreviewSettings?.[widget.id]
                  ) {
                    delete window.widgetPreviewSettings[widget.id].headerColor;
                    if (
                      Object.keys(window.widgetPreviewSettings[widget.id])
                        .length === 0
                    ) {
                      delete window.widgetPreviewSettings[widget.id];
                    }
                  }
                }}
                className="bg-white/10 border-white/20 text-white font-mono text-sm w-24 text-center"
                placeholder="#000000"
              />
              <div
                className="w-6 h-6 rounded-full border-2 border-white/20 cursor-pointer hover:scale-110 transition-transform"
                style={{
                  backgroundColor: getCurrentColor("headerColor"),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setActiveColorSetting({
                    key: "headerColor",
                    label: "Header Text Color",
                    backStep: "text-colors",
                  });
                  setCurrentStep("color-picker");
                }}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-3">
            <Label className="text-white text-sm">Paragraph color</Label>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                value={getCurrentColor("paragraphColor")}
                onChange={(e) => {
                  handleColorInputChange("paragraphColor", e.target.value);
                }}
                onBlur={(e) => {
                  // Save immediately when user finishes typing
                  console.log(
                    `💾 [Save] Paragraph color input blur - saving:`,
                    e.target.value
                  );
                  updateWidgetSettings("paragraphColor", e.target.value);

                  // Clear preview state after save
                  if (
                    typeof window !== "undefined" &&
                    window.widgetPreviewSettings?.[widget.id]
                  ) {
                    delete window.widgetPreviewSettings[widget.id]
                      .paragraphColor;
                    if (
                      Object.keys(window.widgetPreviewSettings[widget.id])
                        .length === 0
                    ) {
                      delete window.widgetPreviewSettings[widget.id];
                    }
                  }
                }}
                className="bg-white/10 border-white/20 text-white font-mono text-sm w-24 text-center"
                placeholder="#000000"
              />
              <div
                className="w-6 h-6 rounded-full border-2 border-white/20 cursor-pointer hover:scale-110 transition-transform"
                style={{
                  backgroundColor: getCurrentColor("paragraphColor"),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setActiveColorSetting({
                    key: "paragraphColor",
                    label: "Paragraph Text Color",
                    backStep: "text-colors",
                  });
                  setCurrentStep("color-picker");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedStep = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setCurrentStep("main");
          }}
          className="flex items-center text-white/50 hover:text-white cursor-pointer text-sm"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3 mr-1" />
          <span>Back</span>
        </div>
        <span className="text-sm font-medium text-white/70 ml-2">
          Advanced Settings
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-white mb-2 block">Border Radius</Label>
          <div className="relative">
            <Input
              type="number"
              value={localBorderRadius}
              onChange={(e) => {
                // Convert to number and back to string to remove leading zeros
                const newValue = Number(e.target.value);
                if (!isNaN(newValue)) {
                  setLocalBorderRadius(newValue);
                  // Update widget in real-time for preview
                  updateWidgetSettingsRealTime("borderRadius", `${newValue}px`);
                  // Force the input to show the cleaned number
                  e.target.value = newValue.toString();
                }
              }}
              onBlur={() => {
                // Save on blur
                updateWidgetSettings("borderRadius", `${localBorderRadius}px`);
              }}
              className="bg-white/5 border-white/10 text-white pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="24"
              min="0"
              step="1"
            />
            <div className="absolute right-8 top-0 bottom-0 flex items-center gap-0.5 px-1">
              <button
                className="group p-1 hover:bg-white/5 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  const currentValue = getValueInPx(settings.borderRadius);
                  const newValue = currentValue + 5;
                  handleSettingChange("borderRadius", `${newValue}px`);
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className="h-2.5 w-2.5 text-white/40 group-hover:text-white/90 transition-colors"
                />
              </button>
              <button
                className="group p-1 hover:bg-white/5 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  const currentValue = getValueInPx(settings.borderRadius);
                  const newValue = Math.max(currentValue - 5, 0);
                  handleSettingChange("borderRadius", `${newValue}px`);
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="h-2.5 w-2.5 text-white/40 group-hover:text-white/90 transition-colors"
                />
              </button>
            </div>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
              px
            </span>
          </div>
          <div className="text-xs text-white/60 mt-1">
            Default: 1.5rem (24px)
          </div>
        </div>

        {widget.widget_type.type !== "single_media" && (
          <div>
            <Label className="text-white mb-2 block">Border Width</Label>
            <div className="relative">
              <Input
                type="number"
                value={localBorderWidth}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (!isNaN(newValue)) {
                    setLocalBorderWidth(newValue);
                    // Real-time preview
                    updateWidgetSettingsRealTime(
                      "borderWidth",
                      `${newValue}px`
                    );
                    // Clean the input to remove leading zeros
                    e.target.value = newValue.toString();
                  }
                }}
                onBlur={() => {
                  // Save on blur
                  updateWidgetSettings("borderWidth", `${localBorderWidth}px`);
                }}
                className="bg-white/5 border-white/10 text-white pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
                min="0"
                step="1"
              />
              <div className="absolute right-8 top-0 bottom-0 flex items-center gap-0.5 px-1">
                <button
                  className="group p-1 hover:bg-white/5 rounded transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    const currentValue = Number(localBorderWidth) || 1;
                    const newValue = currentValue + 1;
                    setLocalBorderWidth(newValue);
                    updateWidgetSettingsRealTime(
                      "borderWidth",
                      `${newValue}px`
                    );
                    handleSettingChange("borderWidth", `${newValue}px`);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faChevronUp}
                    className="h-2.5 w-2.5 text-white/40 group-hover:text-white/90 transition-colors"
                  />
                </button>
                <button
                  className="group p-1 hover:bg-white/5 rounded transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    const currentValue = Number(localBorderWidth) || 1;
                    const newValue = Math.max(currentValue - 1, 0);
                    setLocalBorderWidth(newValue);
                    updateWidgetSettingsRealTime(
                      "borderWidth",
                      `${newValue}px`
                    );
                    handleSettingChange("borderWidth", `${newValue}px`);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="h-2.5 w-2.5 text-white/40 group-hover:text-white/90 transition-colors"
                  />
                </button>
              </div>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
                px
              </span>
            </div>
          </div>
        )}

        <div>
          <Label className="text-white mb-2 block">Transparency</Label>
          <Slider
            value={[getCurrentTransparency()]}
            onValueChange={handleTransparencyChange}
            onValueCommit={handleTransparencyEnd}
            onPointerDown={handleTransparencyStart}
            max={100}
            step={1}
            className="w-full"
          />
          <div className="text-sm text-white/60 mt-1">
            {getCurrentTransparency()}%
          </div>
        </div>

        {widget.widget_type.type === "single_media" && (
          <div className="space-y-2">
            <Label className="text-white">Additional Image Settings</Label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={settings.rounded}
                onCheckedChange={(checked) =>
                  handleSettingChange("rounded", checked)
                }
              />
              <Label className="text-white">Rounded Corners</Label>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const steps = {
    main: renderMainStep,
    "widget-colors": renderWidgetColorStep,
    "text-colors": renderTextColorStep,
    "color-picker": renderColorPickerStep,
    advanced: renderAdvancedStep,
  };

  return (
    <TooltipProvider>
      <div
        className="space-y-6"
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {steps[currentStep]()}
      </div>
    </TooltipProvider>
  );
};

LayoutSettings.displayName = "LayoutSettings";

export default memo(LayoutSettings, (prevProps, nextProps) => {
  // If widget IDs are different, always re-render
  if (prevProps.widget.id !== nextProps.widget.id) {
    return false;
  }

  // If the entire widget object reference changed, re-render
  if (prevProps.widget !== nextProps.widget) {
    return false;
  }

  // If allWidgets length changed, re-render
  if (
    (prevProps.allWidgets?.length || 0) !== (nextProps.allWidgets?.length || 0)
  ) {
    return false;
  }

  // If handleWidgetUpdate function changed, re-render
  if (prevProps.handleWidgetUpdate !== nextProps.handleWidgetUpdate) {
    return false;
  }

  // Props are equal, don't re-render
  return true;
});
